import {
	Box,
	Button,
	Card,
	CardContent,
	Container,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { extractUpdatedFields, isEmptyObject } from "nhb-toolbox";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import ModalsWrapper from "src/components/Modals/ModalsWrapper";
import FullScreenSpinner from "src/components/Spinner/FullScreenSpinner";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import { useValidImage } from "src/hooks/useValidImage";
import { toast } from "src/Toaster/Toaster";
import {
	IAdminData,
	type ISingleAdmin,
} from "src/Types/module/admin/adminTypes";
import SingleDetailsInfo from "./SingleDetailsinfo";

interface IAdminRoles {
	roles_id: number;
	roles_name: string;
}

const SingleAdmin = () => {
	const { id } = useParams();
	const [showModal, setShowModal] = useState<boolean>(false);

	const { data: roles = [], isLoading: isRolesLoading } = useApiQuery<
		IAdminRoles[]
	>({
		endPoint: `/api/admin/get/roles`,
		queryKeys: ["AdminRoles"],
		params: {},
	});

	const [adminData, setAdminData] = useState<IAdminData>({
		role: "",
		status: "",
		user_admin_name: "",
		user_admin_phone: "",
	});

	const { data: admin, isLoading } = useApiQuery<ISingleAdmin>({
		endPoint: `/api/admin/get/single/admin/${id}`,
		queryKeys: ["Admin", id],
	});

	const {
		user_admin_role,
		user_admin_status,
		user_admin_name,
		user_admin_phone,
	} = admin || {};

	useEffect(() => {
		if (admin) {
			setAdminData({
				role: user_admin_role ?? "",
				status: user_admin_status ?? "",
				user_admin_name: user_admin_name ?? "",
				user_admin_phone: user_admin_phone ?? "",
			});
		}
	}, [admin, user_admin_role, user_admin_status]);

	const { mutate: updateAdmin, isPending } = useApiMutation<
		Record<string, unknown>,
		void
	>({
		method: "PATCH",
		endPoint: `/api/admin/${id}`,
		invalidateQueries: ["Admins", "Admin", id],
	});

	const handleUpdate = () => {
		const data = {
			user_admin_role: adminData.role as number,
			user_admin_status: adminData.status,
			user_admin_name: adminData.user_admin_name,
			user_admin_phone: adminData.user_admin_phone,
		};

		const updated = extractUpdatedFields(
			{
				user_admin_role,
				user_admin_status,
				user_admin_name,
				user_admin_phone,
			},
			data
		);

		if (isEmptyObject(updated)) {
			return toast.fire({
				icon: "warning",
				title: "Nothing to update!",
				position: "top",
			});
		}

		updateAdmin(updated);
		setShowModal(false);
	};

	const imgSrc = useValidImage(admin?.user_admin_photo);

	if (isLoading || isRolesLoading) {
		return <FullScreenSpinner />;
	}

	return (
		<div>
			<Helmet>
				<title>BTTF - Single Admin</title>
			</Helmet>
			<Container sx={{ mt: 4 }} maxWidth='lg'>
				<Card>
					<CardContent>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography variant='h4' color='text.primary'>
								Admin: {admin.user_admin_name}
							</Typography>
							<Button
								onClick={() => setShowModal(true)}
								variant='text'
							>
								Edit
							</Button>
						</Box>

						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: "column", sm: "row" },
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: "5px",
							}}
						>
							<figure
								style={{
									aspectRatio: 1,
									maxWidth: "240px",
									width: "100%",
								}}
							>
								<img
									style={{
										aspectRatio: 1,
										maxWidth: "240px",
										objectFit: "contain",
									}}
									src={imgSrc}
								/>
							</figure>

							<SingleDetailsInfo singleAdmin={admin} />
						</Box>

						{/* modals wrapper */}

						<ModalsWrapper
							showModal={showModal}
							setShowModal={setShowModal}
							modalData={{ title: "Edit Member Info" }}
							maxWidth='xs'
						>
							<Box sx={{ width: "100%" }}>
								<FormControl
									required
									sx={{ width: "100%", minWidth: "150px" }}
									margin='normal'
								>
									<InputLabel id='role'>Role</InputLabel>
									<Select
										labelId='role'
										id='role'
										value={adminData.role}
										label='Select Admin Role'
										onChange={(e) => {
											setAdminData({
												...adminData,
												role: e.target.value,
											});
										}}
									>
										{roles?.map((role) => (
											<MenuItem
												key={role.roles_id}
												value={role.roles_id}
											>
												{role.roles_name}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<FormControl
									required
									margin='normal'
									sx={{
										width: "100%",
										minWidth: "150px",
									}}
								>
									<InputLabel
										required
										id='demo-simple-select-label'
									>
										Status
									</InputLabel>
									<Select
										labelId='demo-simple-select-label'
										id='demo-simple-select'
										value={adminData.status}
										label='Status'
										required
										onChange={(e) => {
											setAdminData({
												...adminData,
												status: e.target.value,
											});
										}}
									>
										<MenuItem value='pending'>
											Pending
										</MenuItem>
										<MenuItem value='active'>
											Active
										</MenuItem>
										<MenuItem value='rejected'>
											Rejected
										</MenuItem>
									</Select>
								</FormControl>

								<Grid item xs={12} md={6} lg={3}>
									<TextField
										margin='normal'
										required
										fullWidth
										id='name'
										label='User Admin Name'
										value={adminData?.user_admin_name}
										type='text'
										autoFocus
										onChange={(e) => {
											setAdminData({
												...adminData,
												user_admin_name: e.target.value,
											});
										}}
									/>
								</Grid>
								<Grid item xs={12} md={6} lg={3}>
									<TextField
										margin='normal'
										required
										fullWidth
										id='phone'
										label='Phone'
										value={adminData?.user_admin_phone}
										type='number'
										autoFocus
										onChange={(e) => {
											setAdminData({
												...adminData,
												user_admin_phone:
													e.target.value,
											});
										}}
									/>
								</Grid>

								<Box mt={3} textAlign='center'>
									<Button
										onClick={() => handleUpdate()}
										variant='contained'
									>
										{isPending ? "Updating..." : "Submit"}
									</Button>
								</Box>
							</Box>
						</ModalsWrapper>
					</CardContent>
				</Card>
			</Container>
		</div>
	);
};

export default SingleAdmin;
